import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useParams } from 'react-router-dom';
import { Tooltip } from 'reactstrap';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import Button from '../../components/Button';
import ProfileSummary from '../../components/ProfileSummary';
import ProfileCertifications from '../../components/ProfileCertifications';
import ProfileOrganisations from '../../components/ProfileOrganisations';
import ProfileComplaints from '../../components/ProfileComplaints';
import ProfileFeedback from '../../components/ProfileFeedback';
import ProfileIncidents from '../../components/ProfileIncidents';
import ProfilePolicies from '../../components/ProfilePolicies';
import ProfileChecklists from '../../components/ProfileChecklists';
import ProfileTraining from '../../components/ProfileTraining';
import SingleSelect from '../../components/SingleSelect';
import LoadingSpinner from '../../components/LoadingSpinner';
import GlobalModal from '../../components/GlobalModal';
import RemoveStaffModal from './RemoveStaffModal';

// ICONS
import { ReactComponent as InfoIcon } from '../../assets/svg/Information.svg';

// TYPES
import { RouteParams } from '../../Interfaces';
import { StaffFetchStaffMember, StaffOrganisationAdminCommand, StaffStaffTypeCommand } from '../../api/models';

const StaffProfile: React.FC = () => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get all Staff Types from Redux store
	const staffTypes = useAppSelector((state) => state.lists.staffTypes);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// All Error State
	const [showStaffRoleError, setShowStaffRoleError] = React.useState<string>('');

	const [showAdminStatusError, setShowAdminStatusError] = React.useState<string>('');

	// Set up Data state and Initial Data
	const initialStaffProfileData: StaffFetchStaffMember = {
		id: undefined,
		childAwareId: undefined,
		firstName: undefined,
		lastName: undefined,
		email: undefined,
		profileImage: undefined,
		staffType: undefined,
		isCurrentStaff: undefined,
		isOrganisationAdmin: undefined,
		isYou: undefined,
		canUpdate: undefined,

		certifications: [],
		organisations: [],
		complaints: [],
		feedback: [],
		incidents: [],
		policies: [],
		checklists: [],
		trainings: []
	};

	const [staffProfileData, setStaffProfileData] = React.useState<StaffFetchStaffMember>(initialStaffProfileData);

	// Get the id from params
	const params = useParams<RouteParams>();
	const staffId = params.id;

	// Function to  Get staff profile data from api call
	const getStaffProfileData = async () => {
		setIsLoading(true);

		const response = await apibridge.getStaffProfileData(staffId);

		response.result = response.result || initialStaffProfileData;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setStaffProfileData(response.result);
		}
	};

	React.useEffect(() => {
		getStaffProfileData();
	}, []);

	const {
		id,
		childAwareId,
		profileImage,
		firstName,
		lastName,
		email,
		phoneNumber,
		dateOfBirth,

		staffType,
		isCurrentStaff,
		isOrganisationAdmin,

		certifications,
		organisations,
		complaints,
		feedback,
		incidents,
		policies,
		checklists,
		trainings,
		referenceChecks,

		isYou,
		canUpdate
	} = staffProfileData;

	// State and Logic to manage Organisation Role
	const [selectedRole, setSelectedRole] = React.useState<string | undefined>(staffType);

	React.useEffect(() => {
		setSelectedRole(staffType);
	}, [staffType]);

	const assignStaffOrganisationRole = async (id: string, staffType: string) => {
		setIsLoading(true);

		const data: StaffStaffTypeCommand = { id, staffType };

		const response = await apibridge.assignStaffOrganisationRole(data);

		if (response.isError) {
			setIsLoading(false);

			setShowStaffRoleError(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);
		}
	};

	const changeRoleHandler = (val: string) => {
		setSelectedRole(val);

		assignStaffOrganisationRole(staffId, val);
	};

	const assignOrganisationAdminStatus = async (id: string, isOrganisationAdmin: boolean | undefined) => {
		setIsLoading(true);

		const data: StaffOrganisationAdminCommand = { id, isOrganisationAdmin };

		const response = await apibridge.assignOrganisationAdminStatus(data);

		if (response.isError) {
			setIsLoading(false);

			setShowAdminStatusError(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);
		}
	};

	const isChildawareAdminHandler = () => {
		setStaffProfileData((prevState) => ({ ...prevState, isOrganisationAdmin: !isOrganisationAdmin }));

		assignOrganisationAdminStatus(staffId, !isOrganisationAdmin);
	};

	const [adminTooltipOpen, setAdminTooltipOpen] = React.useState<boolean>(false);
	const toggleAdminTooltip = () => setAdminTooltipOpen(!adminTooltipOpen);

	// Maximum number of items per section
	const defaultMaxItems: number = 4;

	// State to manage Remove Staff Modal
	const [showRemoveStaffModal, setShowRemoveStaffModal] = React.useState<boolean>(false);

	return (
		<div className="page-general-layout staff-profile-page">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{/* PROFILE SUMMARY */}
								<ProfileSummary
									id={id}
									profileImage={profileImage}
									firstName={firstName}
									lastName={lastName}
									childAwareId={childAwareId}
									email={email}
									phoneNumber={phoneNumber}
									dateOfBirth={dateOfBirth}
									canUpdate={isYou || false}
								/>

								{/* ORGANISATION ROLES */}
								{canUpdate && (
									<section className="extra-details no-border">
										<div className="detail-left">
											<h2 className="extra-detail-title">Organisation role</h2>
										</div>

										<div className="detail-right">
											{/* Assign Staff Role */}
											<SingleSelect
												value={selectedRole}
												options={staffTypes}
												onChange={(val: string) => changeRoleHandler(val)}
												isDisabled={isOrganisationActive === false}
											/>

											{!!showStaffRoleError ? <div className="form-validation-error">{showStaffRoleError}</div> : null}
										</div>
									</section>
								)}

								{/* PERMISSION LEVELS */}
								{canUpdate && (
									<section className="extra-details no-border pdt-0 pdb-0">
										<div className="detail-left"></div>

										<div className="detail-right">
											{/* Remove Staff */}
											<div className="toggle-section mbtom-30">
												<h3 className="toggle-label">Currently on staff</h3>

												<Button
													disabled={isYou || isOrganisationActive === false}
													className={`toggle-button ${isCurrentStaff ? 'active' : ''}`}
													onClick={() => setShowRemoveStaffModal(true)}
												>
													<div className="controller"></div>
												</Button>
											</div>

											{/* Assign as Leader */}
											<div className="toggle-section">
												<h3 className="toggle-label">
													Assign as Leader
													<InfoIcon className="info-icon" id="childaware-admin-description-tooltip" />
													<Tooltip
														placement="top"
														isOpen={adminTooltipOpen}
														autohide={false}
														target="childaware-admin-description-tooltip"
														toggle={toggleAdminTooltip}
													>
														<div className="tooltip-content">
															Organisation Leaders will be responsible for managing complaints and feedback sent through
															ChildAware. They will be listed on the Leadership page for all organisation members to
															see.
														</div>
													</Tooltip>
												</h3>

												<Button
													disabled={isYou || isOrganisationActive === false}
													className={`toggle-button ${isOrganisationAdmin ? 'active' : ''}`}
													onClick={isChildawareAdminHandler}
												>
													<div className="controller"></div>
												</Button>
											</div>

											{!!showAdminStatusError ? (
												<div className="form-validation-error">{showAdminStatusError}</div>
											) : null}
										</div>
									</section>
								)}

								{/* DOCUMENTS */}
								<ProfileCertifications
									certifications={certifications}
									referenceChecks={referenceChecks}
									defaultMaxItems={defaultMaxItems}
									staffId={staffId}
									canUpdate={canUpdate}
									isYou={isYou}
									getStaffProfileData={getStaffProfileData}
								/>

								{/* ORGANISATIONS */}
								<ProfileOrganisations isYou={isYou} organisations={organisations} defaultMaxItems={defaultMaxItems} />

								{/* COMPLAINTS */}
								<ProfileComplaints complaints={complaints} defaultMaxItems={defaultMaxItems} />

								{/* FEEDBACK */}
								<ProfileFeedback feedback={feedback} defaultMaxItems={defaultMaxItems} />

								{/* INCIDENTS REPORT */}
								<ProfileIncidents incidents={incidents} defaultMaxItems={defaultMaxItems} />

								{/* POLICIES */}
								<ProfilePolicies policies={policies} defaultMaxItems={defaultMaxItems} />

								{/* CHECKLISTS*/}
								<ProfileChecklists checklists={checklists} defaultMaxItems={defaultMaxItems} />

								{/* TRAINING*/}
								<ProfileTraining isYou={isYou} training={trainings} defaultMaxItems={defaultMaxItems} />

								<ScrollToTopButton extraClass="mtop-30" />

								<GlobalModal
									isOpen={showRemoveStaffModal}
									setIsOpen={setShowRemoveStaffModal}
									titleText="Remove from Organisation"
								>
									<RemoveStaffModal
										staffId={staffId}
										getStaffProfileData={getStaffProfileData}
										firstName={firstName}
										lastName={lastName}
									/>
								</GlobalModal>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default StaffProfile;
